/**
 * Vendor-neutral analytics hooks.
 *
 * Any element with data-cr-event="<name>" publishes a Shopify custom event
 * `column_radiators:<name>` when clicked (or, for <summary>, when opened).
 * Components can also dispatch `document.dispatchEvent(new CustomEvent('cr:track',
 * { detail: { event: '<name>', ...data } }))`.
 *
 * Events go through Shopify.analytics.publish, so they reach Web Pixels
 * (Settings > Customer events) and are subject to the store's customer
 * privacy/consent settings. No third-party tag is hardcoded here: GA4, Meta
 * and A/B tools subscribe to these events from a custom pixel.
 *
 * Payload: { ...data-cr-* attributes, component, page_type, path }.
 */
const PREFIX = 'column_radiators:';

function publish(name, data = {}) {
  const publisher = window.Shopify?.analytics?.publish;
  if (typeof publisher !== 'function' || !name) return;
  try {
    publisher(PREFIX + name, {
      ...data,
      page_type: document.body?.dataset.pageType || undefined,
      path: window.location.pathname,
    });
  } catch {
    // Analytics must never break the storefront.
  }
}

function payloadFor(element) {
  const data = {};
  for (const [key, value] of Object.entries(element.dataset)) {
    if (key.startsWith('cr') && key !== 'crEvent' && key !== 'crEventView') {
      data[key.slice(2).replace(/^./, (c) => c.toLowerCase())] = value;
    }
  }
  const component = element.closest('[data-cr-component]');
  if (component instanceof HTMLElement) data.component = component.dataset.crComponent;
  const label = (element.getAttribute('aria-label') || element.textContent || '').trim().replace(/\s+/g, ' ');
  if (label) data.label = label.slice(0, 100);
  if (element instanceof HTMLAnchorElement) data.href = element.getAttribute('href');
  return data;
}

document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;
    const element = event.target.closest('[data-cr-event]');
    if (!(element instanceof HTMLElement)) return;
    // <summary> fires on open only.
    if (element.tagName === 'SUMMARY' && element.parentElement?.hasAttribute('open')) return;
    publish(element.dataset.crEvent, payloadFor(element));
  },
  { capture: true, passive: true }
);

document.addEventListener('cr:track', (event) => {
  if (!(event instanceof CustomEvent) || !event.detail?.event) return;
  const { event: name, ...data } = event.detail;
  publish(name, data);
});

// Elements rendered as the result of an action (e.g. form success messages).
function publishViews() {
  document.querySelectorAll('[data-cr-event-view]').forEach((element) => {
    if (!(element instanceof HTMLElement) || element.dataset.crViewed) return;
    element.dataset.crViewed = 'true';
    const form = element.closest('form');
    const component = element.closest('[data-cr-component]');
    publish(element.dataset.crEventView, {
      form: form?.id || undefined,
      component: component instanceof HTMLElement ? component.dataset.crComponent : undefined,
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', publishViews);
} else {
  publishViews();
}
